/**
 * Trang quản lý khách hàng (managed users) — super admin chỉnh sửa thông tin riêng tư.
 *
 * Bố cục hai cột:
 * - Cột trái: danh sách khách hàng, tìm kiếm client-side, chọn user.
 * - Cột phải: panel chỉnh sửa thông tin riêng tư (địa chỉ, CCCD, tuổi...).
 */
import { useMemo, useState } from 'react';
import { AdminManagedUserCard } from '../../components/admin/AdminManagedUserCard';
import { AdminManagedUserEditPanel } from '../../components/admin/AdminManagedUserEditPanel';
import { AdminUserInlineListSkeleton } from '../../components/admin/AdminUserInlineListSkeleton';
import { AdminUsersListToolbar } from '../../components/admin/AdminUsersListToolbar';
import { AdminUsersSkeleton } from '../../components/admin/AdminUsersSkeleton';
import { EmptyState } from '../../components/Common/EmptyState';
import { ErrorMessage } from '../../components/Common/ErrorMessage';
import { useAdminManagedUsers } from '../../features/admin/managedUsers/hooks/useAdminManagedUsers';
import { filterBySearchText } from '../../shared/utils/filterBySearchText';
import { formatText, TEXT } from '../../shared/languages';

/** Các trường dùng cho tìm kiếm khách hàng trong danh sách */
const MANAGED_USER_SEARCH_FIELDS = [
  'name',
  'email',
  'phone',
  'preferred_province',
  'residential_address',
  'hometown',
  'residence',
  'cccd',
];

/** Trang quản lý khách hàng và chỉnh sửa thông tin riêng tư */
export const AdminManagedUsersPage = () => {
  const {
    userList,
    selectedUser,
    selectedUserId,
    privateForm,
    isLoadingUsers,
    isSaving,
    errorMessage,
    cccdError,
    successMessage,
    selectUser,
    updatePrivateField,
    savePrivateInfo,
  } = useAdminManagedUsers();
  const [searchInput, setSearchInput] = useState('');
  const labels = TEXT.admin.managedUsers;
  const common = TEXT.common;

  /** Lọc danh sách khách hàng theo từ khóa (client-side) */
  const filteredUserList = useMemo(
    () => filterBySearchText(userList, searchInput, MANAGED_USER_SEARCH_FIELDS),
    [searchInput, userList]
  );

  const summaryLabel = useMemo(
    () => formatText(labels.resultsCount, { count: userList.length }),
    [labels.resultsCount, userList.length]
  );

  const hasSearch = Boolean(searchInput.trim());

  if (isLoadingUsers && !userList.length) {
    return <AdminUsersSkeleton />;
  }

  return (
    <div className="admin-users-page admin-managed-users-page">
      <div className="admin-heading admin-users-heading">
        <div className="admin-users-heading-copy">
          <span className="eyebrow">{labels.eyebrow}</span>
          <h1>{labels.title}</h1>
          <p className="admin-users-subtitle">{summaryLabel}</p>
        </div>
      </div>

      {errorMessage && <ErrorMessage message={errorMessage} />}
      {successMessage && <div className="success-box admin-users-success">{successMessage}</div>}

      <div className="admin-users-layout">
        <section className="admin-users-main">
          <AdminUsersListToolbar
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            placeholder={labels.searchPlaceholder}
            listTitle={labels.listTitle}
          />

          {isLoadingUsers ? (
            <AdminUserInlineListSkeleton />
          ) : filteredUserList.length ? (
            <div className="admin-user-list">
              {filteredUserList.map((managedUser) => (
                <AdminManagedUserCard
                  key={managedUser.id}
                  managedUser={managedUser}
                  labels={labels}
                  common={common}
                  isSelected={String(selectedUserId) === String(managedUser.id)}
                  onSelect={selectUser}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title={hasSearch ? labels.noResultsTitle : labels.emptyTitle}
              message={hasSearch ? common.searchNoResultsMessage : labels.emptyMessage}
            />
          )}
        </section>

        <AdminManagedUserEditPanel
          labels={labels}
          common={common}
          selectedUser={selectedUser}
          privateForm={privateForm}
          cccdError={cccdError}
          isSaving={isSaving}
          onFieldChange={updatePrivateField}
          onSubmit={savePrivateInfo}
        />
      </div>
    </div>
  );
};
